import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Button } from 'ui-components/components/button';

import { ConfirmDialogueModal } from '../../../components/modals/confirm-dialogue-modal';
import InventorySourcesDropdown from './inventory-sources-dropdown';
import PillBank from '../../../components/pill-bank';
import { ShipEngine } from '../../../constants/connection-types';
import useConsolidatedInventorySources from './use-consolidated-inventory-sources';
import { useModal } from '../../../hooks/use-modal';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import HelpArticleLink from '../../../components/help-article-link';
import { linkIDs } from '../../../services/link-service/links';
import SaveBar from '../components/save-bar';
import SettingsPageWrapper from '../settings-page-wrapper';

import messages from './messages';
import styles from './styles.module.less';

export const InventorySources = () => {
  const {
    fetchConsolidatedInventorySources,
    saveConsolidatedInventorySources,
    loading,
    data: consolidatedInventorySources,
  } = useConsolidatedInventorySources();
  const [selectedInventorySourceIds, setSelectedInventorySourceIds] = useState([]);
  const { close: closeAddAllConfirmationModal, isOpen: isAddAllConfirmationModalOpen, open: openAddAllConfirmationModal } = useModal();
  const {
    close: closeRemoveAllConfirmationModal,
    isOpen: isRemoveAllConfirmationModalOpen,
    open: openRemoveAllConfirmationModal,
  } = useModal();

  useOnComponentMount(() => {
    fetchConsolidatedInventorySources();
  });

  useEffect(() => {
    setSelectedInventorySourceIds(consolidatedInventorySources?.includedInventorySourceIds ?? []);
  }, [consolidatedInventorySources]);

  const inventorySourcesIds = useMemo(() => {
    const { inventorySources } = consolidatedInventorySources;
    return (inventorySources ?? []).map((source) => source.connectionId);
  }, [consolidatedInventorySources]);

  const inventorySourcesIdMap = useMemo(() => {
    const { inventorySources } = consolidatedInventorySources;
    return (inventorySources ?? []).reduce((hash, source) => {
      return {
        ...hash,
        [source.connectionId]: source,
      };
    }, {});
  }, [consolidatedInventorySources]);

  const toggleLabel = loading ? <FormattedMessage {...messages.loadingLabel} /> : <FormattedMessage {...messages.toggleLabel} />;

  const onUpdateInventorySources = useCallback((sourceIds) => {
    setSelectedInventorySourceIds(sourceIds);
  }, []);

  const onRemoveAllCallback = useCallback(() => {
    setSelectedInventorySourceIds([]);
    closeRemoveAllConfirmationModal();
  }, [closeRemoveAllConfirmationModal]);

  const onAddAllSourcesCallback = useCallback(() => {
    setSelectedInventorySourceIds(inventorySourcesIds);
    closeAddAllConfirmationModal();
  }, [closeAddAllConfirmationModal, inventorySourcesIds]);

  const onAddSourceClicked = useCallback(
    (sourceId) => {
      if (!selectedInventorySourceIds.includes(sourceId)) {
        setSelectedInventorySourceIds([...selectedInventorySourceIds, sourceId]);
      }
    },
    [selectedInventorySourceIds],
  );

  const onSaveClicked = useCallback(() => {
    const excludedSourceIds = inventorySourcesIds.filter((id) => !selectedInventorySourceIds.includes(id));
    saveConsolidatedInventorySources(excludedSourceIds);
  }, [selectedInventorySourceIds, inventorySourcesIds, saveConsolidatedInventorySources]);

  const formatLabel = useCallback(
    (sourceId) => {
      const source = inventorySourcesIdMap[sourceId];
      if (source.connectionType === ShipEngine) {
        return <FormattedMessage {...messages.shipStationInternalInventory} />;
      }
      return source.connectionName;
    },
    [inventorySourcesIdMap],
  );

  return (
    <>
      <SettingsPageWrapper
        messages={{
          headerMessage: messages.pageTitle,
          descriptionMessage: {
            ...messages.pageHelperText,
            values: {
              a: (chunks) => <HelpArticleLink articleId={linkIDs.ViewStockCounts}>{chunks}</HelpArticleLink>,
            },
          },
        }}
      >
        <label className={styles['label-header']}>
          <FormattedMessage {...messages.inventorySourceDropdownLabel} />
        </label>
        <InventorySourcesDropdown
          inventorySources={inventorySourcesIds}
          labelFormatter={formatLabel}
          loading={loading}
          onAddAllSourcesClicked={openAddAllConfirmationModal}
          onAddSourceClicked={onAddSourceClicked}
          toggleLabel={toggleLabel}
        />
        <label
          className={classnames(styles['label-header'], {
            [styles.error]: !selectedInventorySourceIds.length && !loading,
          })}
        >
          <FormattedMessage {...messages.pillBankLabel} />
        </label>
        <PillBank
          className={classnames(styles['pill-bank'], {
            [styles.error]: !selectedInventorySourceIds.length && !loading,
          })}
          labelFormatter={formatLabel}
          onUpdate={onUpdateInventorySources}
          selectedItems={selectedInventorySourceIds}
        />
        {!selectedInventorySourceIds.length && !loading ? (
          <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
            <FormattedMessage {...messages.pillBankError} />
          </InlineMessage>
        ) : (
          <Button className={styles['remove-all-button']} disabled={loading} onClick={openRemoveAllConfirmationModal} theme='link'>
            <FormattedMessage {...messages.removeAllButtonLink} />
          </Button>
        )}
      </SettingsPageWrapper>
      <SaveBar disabled={!selectedInventorySourceIds.length} onSave={onSaveClicked} />
      {isAddAllConfirmationModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...messages.confirmationModalBodyAdd} values={{ count: inventorySourcesIds.length }} />}
          header={<FormattedMessage {...messages.confirmationModalHeader} />}
          onClose={closeAddAllConfirmationModal}
          onConfirm={onAddAllSourcesCallback}
        />
      )}
      {isRemoveAllConfirmationModalOpen && (
        <ConfirmDialogueModal
          body={<FormattedMessage {...messages.confirmationModalBodyRemove} />}
          header={<FormattedMessage {...messages.confirmationModalHeader} />}
          onClose={closeRemoveAllConfirmationModal}
          onConfirm={onRemoveAllCallback}
        />
      )}
    </>
  );
};
